import { useEffect, useState, useCallback, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AppHeader from '../../components/AppHeader';
import StatusBadge from '../../components/StatusBadge';
import AttendanceTimeline from '../../components/AttendanceTimeline';
import { eventsApi, zonesApi, handoverApi } from '../../api';
import { TRIP_STATUS, TRIP_STATUS_BADGE } from '../../utils/trips';

const METHOD_LABEL = { qr: 'QR', manual_ops: 'OpS ruční' };

export default function MuDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [zones, setZones] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [tab, setTab] = useState('zones');
  const [newZone, setNewZone] = useState('');
  const [qrZone, setQrZone] = useState(null);

  // Hierarchie zón
  const [expandedZone, setExpandedZone] = useState(null); // id rozkliknuté zóny (seznam účastníků)
  const [dragZoneId, setDragZoneId] = useState(null);     // právě tažená zóna
  const [dragOverId, setDragOverId] = useState(null);     // cílová zóna pod kurzorem
  const [dragOverRoot, setDragOverRoot] = useState(false);// plocha pro odpojení rodiče
  const [zoneErr, setZoneErr] = useState('');

  // Checkpointy
  const [checkpoints, setCheckpoints] = useState([]);
  const [newCpName, setNewCpName] = useState('');
  const [cpDetail, setCpDetail] = useState(null); // { checkpoint, events }
  const [cpLoading, setCpLoading] = useState(false);

  // Dashboard "na cestě"
  const [dashboard, setDashboard] = useState(null);
  // Přehled materiálu (na cestě vs na místě)
  const [material, setMaterial] = useState(null);
  const [openMatBox, setOpenMatBox] = useState(null);
  // Časová osa příchodů/odchodů
  const [timeline, setTimeline] = useState(null);

  const loadZones = useCallback(() => zonesApi.list(id).then((r) => setZones(r.data)).catch(() => {}), [id]);
  const loadParticipants = useCallback(() => eventsApi.participants(id).then((r) => setParticipants(r.data)).catch(() => {}), [id]);
  const loadCheckpoints = useCallback(() => handoverApi.list(id).then((r) => setCheckpoints(r.data)).catch(() => {}), [id]);
  const loadDashboard = useCallback(() => handoverApi.dashboard(id).then((r) => setDashboard(r.data)).catch(() => {}), [id]);
  const loadMaterial = useCallback(() => eventsApi.materialOverview(id).then((r) => setMaterial(r.data)).catch(() => setMaterial({ items: [], boxes: [] })), [id]);
  const loadTimeline = useCallback(() => eventsApi.timeline(id).then((r) => setTimeline(r.data)).catch(() => setTimeline([])), [id]);

  useEffect(() => {
    eventsApi.get(id).then((r) => setEvent(r.data)).catch(() => navigate('/mu'));
    loadZones();
    loadParticipants();
  }, [id]);

  useEffect(() => {
    const t = setInterval(loadParticipants, 5000);
    return () => clearInterval(t);
  }, [loadParticipants]);

  useEffect(() => {
    if (tab === 'checkpoints') { loadCheckpoints(); setCpDetail(null); }
    if (tab === 'dashboard') loadDashboard();
    if (tab === 'material') { loadMaterial(); setOpenMatBox(null); }
    if (tab === 'timeline') loadTimeline();
  }, [tab]);

  const addZone = async (e) => {
    e.preventDefault();
    if (!newZone.trim()) return;
    await zonesApi.create(id, newZone.trim());
    setNewZone('');
    loadZones();
  };
  const delZone = async (zid) => {
    if (confirm('Smazat zónu? Smaže se i její historie.')) { await zonesApi.delete(zid); loadZones(); }
  };

  // ── Hierarchie zón ──────────────────────────────────────────────────────────
  const rootZones = zones.filter((z) => !z.parent_zone_id);
  const childrenOf = (zid) => zones.filter((z) => z.parent_zone_id === zid);
  // Sčítaný počet přítomných: vlastní zóna + všechny její podzóny
  const zoneTotal = (z) => (z.active_count || 0) + childrenOf(z.id).reduce((s, c) => s + (c.active_count || 0), 0);
  // Účastníci aktuálně přítomní (IN) v dané zóně
  const presentInZone = (zid) => participants.filter((p) => p.zone_id === zid && p.status === 'IN');

  const setParent = async (zoneId, parentZoneId) => {
    setZoneErr('');
    try {
      await zonesApi.setParent(zoneId, parentZoneId);
      loadZones();
    } catch (err) {
      setZoneErr(err.response?.data?.error || 'Změnu hierarchie se nepodařilo uložit.');
    }
  };
  const addSubZone = async (parentId, name) => {
    if (!name || !name.trim()) return;
    setZoneErr('');
    try {
      await zonesApi.create(id, name.trim(), parentId);
      loadZones();
    } catch (err) {
      setZoneErr(err.response?.data?.error || 'Podzónu se nepodařilo vytvořit.');
    }
  };

  // Drag & drop (desktop). Tažení zóny na jinou kořenovou = vnoření; na šedou plochu = odpojení.
  const onDrop = (targetParentId) => {
    if (dragZoneId && dragZoneId !== targetParentId) setParent(dragZoneId, targetParentId);
    setDragZoneId(null); setDragOverId(null); setDragOverRoot(false);
  };

  // Vykreslení jedné zóny (karta). isChild = odsazená podzóna.
  const renderZone = (z, isChild) => {
    const present = presentInZone(z.id);
    const expanded = expandedZone === z.id;
    const isDropTarget = !isChild && dragOverId === z.id && dragZoneId && dragZoneId !== z.id;
    return (
      <div
        key={z.id}
        className="card"
        draggable
        onDragStart={() => setDragZoneId(z.id)}
        onDragEnd={() => { setDragZoneId(null); setDragOverId(null); setDragOverRoot(false); }}
        onDragOver={!isChild ? (e) => { e.preventDefault(); setDragOverId(z.id); } : undefined}
        onDrop={!isChild ? (e) => { e.preventDefault(); onDrop(z.id); } : undefined}
        style={{
          marginLeft: isChild ? 28 : 0,
          opacity: dragZoneId === z.id ? 0.5 : 1,
          border: isDropTarget ? '2px dashed var(--red, #c0392b)' : undefined,
          background: isDropTarget ? 'rgba(192,57,43,0.06)' : undefined,
          cursor: 'grab',
        }}
      >
        <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>
            <span style={{ opacity: 0.5, marginRight: 6 }}>⠿</span>
            {isChild && <span style={{ opacity: 0.5, marginRight: 4 }}>↳</span>}
            {z.name}
          </strong>
          <button className="badge badge-in" style={{ border: 0, cursor: 'pointer' }}
            title="Zobrazit přítomné"
            onClick={() => setExpandedZone(expanded ? null : z.id)}>
            {isChild ? (z.active_count || 0) : zoneTotal(z)} 👤
            {!isChild && childrenOf(z.id).length > 0 && (z.active_count || 0) !== zoneTotal(z) &&
              <span style={{ opacity: 0.8, fontSize: '0.7rem' }}> (vč. podzón)</span>}
          </button>
        </div>
        <p className="text-muted" style={{ fontSize: '0.8rem', margin: '6px 0' }}>kód: {z.slug}</p>

        {expanded && (
          <div style={{ margin: '8px 0', borderTop: '1px solid var(--border, #eee)', paddingTop: 8 }}>
            {present.length === 0
              ? <p className="text-muted" style={{ fontSize: '0.8rem' }}>Nikdo právě přítomen.</p>
              : present.map((p) => (
                  <div key={p.id} style={{ fontSize: '0.85rem', padding: '2px 0' }}>
                    👤 {p.first_name} {p.last_name}
                    {p.organization && <span className="text-muted" style={{ marginLeft: 6, fontSize: '0.78rem' }}>{p.organization}</span>}
                  </div>
                ))}
          </div>
        )}

        <div className="flex gap-8" style={{ marginTop: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={() => setQrZone(z)}>QR</button>
          <a className="btn btn-outline btn-sm" href={zonesApi.qrSinglePrintUrl(z.id)} target="_blank" rel="noreferrer">Tisk</a>
          {/* Fallback k drag-and-drop pro dotyk: přesun pod jinou kořenovou zónu / odpojení */}
          <select
            className="form-input"
            style={{ width: 'auto', padding: '2px 6px', fontSize: '0.8rem' }}
            value={z.parent_zone_id || ''}
            onChange={(e) => setParent(z.id, e.target.value || null)}
            title="Přesunout pod zónu"
            disabled={childrenOf(z.id).length > 0}
          >
            <option value="">— bez nadřazené —</option>
            {rootZones.filter((r) => r.id !== z.id).map((r) => (
              <option key={r.id} value={r.id}>pod: {r.name}</option>
            ))}
          </select>
          <button className="btn btn-danger btn-sm" onClick={() => delZone(z.id)}>×</button>
        </div>
      </div>
    );
  };

  const toggleActive = async () => {
    const updated = await eventsApi.update(id, { ...event, active: !event.active });
    setEvent(updated.data);
  };

  const addCheckpoint = async (e) => {
    e.preventDefault();
    if (!newCpName.trim()) return;
    await handoverApi.create({ event_id: parseInt(id), name: newCpName.trim() });
    setNewCpName('');
    loadCheckpoints();
  };

  const deleteCheckpoint = async (cpId) => {
    if (!confirm('Smazat checkpoint? Smaže se i jeho historie skenů.')) return;
    await handoverApi.delete(cpId);
    loadCheckpoints();
    if (cpDetail?.checkpoint?.id === cpId) setCpDetail(null);
  };

  const openCpDetail = async (cp) => {
    setCpLoading(true);
    try {
      const evs = await handoverApi.checkpointEvents(cp.id);
      setCpDetail({ checkpoint: cp, events: evs.data });
    } finally {
      setCpLoading(false);
    }
  };

  const rotateToken = async (cpId) => {
    if (!confirm('Rotovat token? Tím se invalidují všechny dosud vytisknuté QR kódy tohoto checkpointu.')) return;
    await handoverApi.rotateToken(cpId);
    loadCheckpoints();
    if (cpDetail?.checkpoint?.id === cpId) setCpDetail(null);
  };

  if (!event) return <div className="container" style={{ paddingTop: 48 }}><div className="spinner" /></div>;

  const dashStatusOrder = ['en_route', 'arrived', 'planned', 'completed', 'cancelled'];
  const dashStatusIcons = { planned: '🕐', en_route: '🚚', arrived: '📍', completed: '✅', cancelled: '❌' };

  return (
    <div>
      <AppHeader subtitle={event.name} />
      <div className="container-wide" style={{ paddingTop: 24 }}>
        <div className="card mb-16">
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2>{event.name}</h2>
              {event.location && <p className="text-muted">📍 {event.location}</p>}
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>{new Date(event.event_date).toLocaleString('cs-CZ')}</p>
            </div>
            <div className="flex gap-8">
              <button className="btn btn-sm btn-outline" onClick={() => navigate(`/mu/${id}/mapa`)}>🗺️ Mapa</button>
              <button className={`btn btn-sm ${event.active ? 'btn-outline' : 'btn-success'}`} onClick={toggleActive}>
                {event.active ? 'Deaktivovat' : 'Aktivovat'}
              </button>
            </div>
          </div>
        </div>

        <div className="admin-nav" style={{ marginBottom: 16 }}>
          <button className={tab === 'zones' ? 'active' : ''} onClick={() => setTab('zones')}>Zóny ({zones.length})</button>
          <button className={tab === 'participants' ? 'active' : ''} onClick={() => setTab('participants')}>Účastníci ({participants.length})</button>
          <button className={tab === 'checkpoints' ? 'active' : ''} onClick={() => setTab('checkpoints')}>Checkpointy</button>
          <button className={tab === 'dashboard' ? 'active' : ''} onClick={() => setTab('dashboard')}>Na cestě</button>
          <button className={tab === 'material' ? 'active' : ''} onClick={() => setTab('material')}>Materiál</button>
          <button className={tab === 'timeline' ? 'active' : ''} onClick={() => setTab('timeline')}>Časová osa</button>
        </div>

        {/* ─── ZÓNY ─────────────────────────────────────────────────────────── */}
        {tab === 'zones' && (
          <>
            <div className="card mb-16">
              <form className="flex gap-8" onSubmit={addZone}>
                <input className="form-input flex-1" placeholder="Název zóny" value={newZone} onChange={(e) => setNewZone(e.target.value)} />
                <button className="btn btn-primary">+ Přidat zónu</button>
              </form>
            </div>
            {zoneErr && <div className="alert alert-warning mb-16">{zoneErr}</div>}

            {zones.length === 0 && <p className="text-muted">Zatím žádné zóny.</p>}

            {rootZones.length > 0 && (
              <p className="text-muted" style={{ fontSize: '0.8rem', marginBottom: 8 }}>
                💡 Přetáhněte zónu na jinou (nebo použijte výběr „pod:") pro vytvoření podzóny. Klikem na počet 👤 zobrazíte přítomné.
              </p>
            )}

            {/* Strom: kořenové zóny + jejich podzóny */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 640 }}>
              {rootZones.map((root) => (
                <div key={root.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {renderZone(root, false)}
                  {childrenOf(root.id).map((child) => renderZone(child, true))}
                </div>
              ))}
            </div>

            {/* Plocha pro odpojení nadřazené zóny (drag sem) */}
            {zones.some((z) => z.parent_zone_id) && (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOverRoot(true); }}
                onDragLeave={() => setDragOverRoot(false)}
                onDrop={(e) => { e.preventDefault(); onDrop(null); }}
                style={{
                  marginTop: 16, maxWidth: 640, padding: 16, textAlign: 'center', borderRadius: 8,
                  border: `2px dashed ${dragOverRoot ? 'var(--red, #c0392b)' : 'var(--border, #ccc)'}`,
                  background: dragOverRoot ? 'rgba(192,57,43,0.06)' : 'transparent',
                  color: '#888', fontSize: '0.82rem',
                }}
              >
                ⬇ Sem přetáhněte zónu pro odpojení od nadřazené (udělat ji kořenovou)
              </div>
            )}
          </>
        )}

        {/* ─── ÚČASTNÍCI ────────────────────────────────────────────────────── */}
        {tab === 'participants' && (
          <>
          <div className="flex" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
            {participants.length > 0 && <a className="btn btn-outline btn-sm" href={eventsApi.participantsCsvUrl(id)} download>⬇ CSV</a>}
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Jméno</th><th>Spolek</th><th>Zóna</th><th>Stav</th><th>Čas</th></tr></thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.id}>
                    <td>{p.first_name} {p.last_name}</td>
                    <td>{p.organization || ''}</td>
                    <td>{p.zone_name || ''}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td className="text-muted" style={{ fontSize: '0.85rem' }}>{p.timestamp ? new Date(p.timestamp).toLocaleString('cs-CZ') : ''}</td>
                  </tr>
                ))}
                {participants.length === 0 && <tr><td colSpan={5} className="text-muted text-center">Zatím nikdo.</td></tr>}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* ─── CHECKPOINTY ──────────────────────────────────────────────────── */}
        {tab === 'checkpoints' && (
          <div style={{ display: 'grid', gridTemplateColumns: cpDetail ? '1fr 1fr' : '1fr', gap: 16, alignItems: 'start' }}>
            <div>
              <div className="card mb-16">
                <form className="flex gap-8" onSubmit={addCheckpoint}>
                  <input className="form-input flex-1" placeholder="Název checkpointu (např. Příjezdová brána)" value={newCpName} onChange={(e) => setNewCpName(e.target.value)} />
                  <button className="btn btn-primary">+ Přidat</button>
                </form>
              </div>

              {checkpoints.length === 0 ? (
                <p className="text-muted">Zatím žádné checkpointy. Přidejte první výše.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {checkpoints.map((cp) => (
                    <div key={cp.id} className="card">
                      <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <strong>{cp.name}</strong>
                          <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>
                            kód: {cp.slug} · {cp.scan_count} skenů
                          </div>
                        </div>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteCheckpoint(cp.id)}>×</button>
                      </div>
                      <div className="flex gap-8" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                        <a className="btn btn-primary btn-sm" href={handoverApi.qrUrl(cp.id)} download>⬇ QR PNG</a>
                        <button className="btn btn-outline btn-sm" onClick={() => openCpDetail(cp)}>Historie skenů</button>
                        <button className="btn btn-outline btn-sm" onClick={() => rotateToken(cp.id)}>🔄 Rotovat token</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cpDetail && (
              <div className="card">
                <div className="flex" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                  <h3>{cpDetail.checkpoint.name} – skeny</h3>
                  <button className="btn btn-outline btn-sm" onClick={() => setCpDetail(null)}>×</button>
                </div>
                {cpLoading ? <div className="spinner" /> : cpDetail.events.length === 0 ? (
                  <p className="text-muted text-center">Zatím žádné skeny.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {cpDetail.events.map((ev) => (
                      <div key={ev.id} style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: 8 }}>
                        <div style={{ fontWeight: 600 }}>{ev.vehicle_name} {ev.vehicle_plate ? `· ${ev.vehicle_plate}` : ''}</div>
                        <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                          {ev.chapter_name}
                          {ev.confirmed_first_name ? ` · potvrdil: ${ev.confirmed_first_name} ${ev.confirmed_last_name}` : ''}
                          · {METHOD_LABEL[ev.method] || ev.method}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>{new Date(ev.created_at).toLocaleString('cs-CZ')}</div>
                        {ev.note && <div style={{ fontSize: '0.82rem', marginTop: 2, fontStyle: 'italic' }}>{ev.note}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── DASHBOARD NA CESTĚ ───────────────────────────────────────────── */}
        {tab === 'dashboard' && (
          <div>
            {!dashboard ? (
              <div className="spinner" />
            ) : (
              <>
                {dashStatusOrder.map((status) => {
                  const trips = dashboard[status] || [];
                  if (trips.length === 0) return null;
                  return (
                    <div key={status} className="mb-16">
                      <h3 className="mb-8" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{dashStatusIcons[status]}</span>
                        <span>{TRIP_STATUS[status]}</span>
                        <span className="badge badge-none" style={{ fontSize: '0.8rem' }}>{trips.length}</span>
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                        {trips.map((t) => (
                          <Link key={t.id} to={`/cesty/${t.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="card" style={{ cursor: 'pointer' }}>
                              <div className="flex" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                                <strong>{t.vehicle_name}</strong>
                                {t.vehicle_plate && <span className="text-muted" style={{ fontSize: '0.82rem' }}>{t.vehicle_plate}</span>}
                              </div>
                              <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                                {t.chapter_name}
                                {t.driver_first_name ? ` · ${t.driver_first_name} ${t.driver_last_name}` : ''}
                              </div>
                              {(t.origin || t.destination) && (
                                <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>
                                  {t.origin && `z: ${t.origin}`}{t.origin && t.destination && ' → '}{t.destination && `do: ${t.destination}`}
                                </div>
                              )}
                              <div className="flex gap-8" style={{ marginTop: 6, fontSize: '0.78rem' }}>
                                {t.cargo_count > 0 && <span>📦 {t.cargo_count} pol.</span>}
                                {t.handover_count > 0 && <span>✅ {t.handover_count}× předáno</span>}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {dashStatusOrder.every((s) => (dashboard[s] || []).length === 0) && (
                  <p className="text-muted text-center">Žádné cesty přiřazené k této MU.</p>
                )}
                <div className="flex gap-8 mt-8" style={{ justifyContent: 'center' }}>
                  <button className="btn btn-outline btn-sm" onClick={loadDashboard}>🔄 Obnovit</button>
                  <a className="btn btn-outline btn-sm" href={handoverApi.csvUrl(id)} download>⬇ Export předání (CSV)</a>
                </div>
              </>
            )}
          </div>
        )}

        {/* ─── MATERIÁL (na cestě vs na místě) ──────────────────────────────── */}
        {tab === 'material' && (
          <div>
            {!material ? (
              <div className="spinner" />
            ) : (material.items.length === 0 && material.boxes.length === 0) ? (
              <p className="text-muted text-center">Na tuto MU zatím není naložen ani dovezen žádný materiál.</p>
            ) : (
              <>
                <div className="flex" style={{ justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
                  <a className="btn btn-outline btn-sm" href={eventsApi.materialCsvUrl(id)} download>⬇ CSV</a>
                  <button className="btn btn-outline btn-sm" onClick={loadMaterial}>🔄 Obnovit</button>
                </div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Položka / box</th>
                        <th style={{ textAlign: 'right' }}>🚚 Na cestě</th>
                        <th style={{ textAlign: 'right' }}>✅ Na místě</th>
                        <th style={{ textAlign: 'right' }}>Celkem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {material.items.map((m, i) => (
                        <tr key={'i' + i}>
                          <td>{m.item_name}</td>
                          <td style={{ textAlign: 'right' }}>{m.enroute_qty > 0 ? <strong>{m.enroute_qty} {m.unit}</strong> : <span className="text-muted">—</span>}</td>
                          <td style={{ textAlign: 'right' }}>{m.delivered_qty > 0 ? <strong style={{ color: 'var(--color-success, green)' }}>{m.delivered_qty} {m.unit}</strong> : <span className="text-muted">—</span>}</td>
                          <td style={{ textAlign: 'right' }} className="text-muted">{m.enroute_qty + m.delivered_qty} {m.unit}</td>
                        </tr>
                      ))}
                      {material.boxes.map((b) => (
                        <Fragment key={b.cargo_id}>
                          <tr style={{ cursor: 'pointer' }} onClick={() => setOpenMatBox(openMatBox === b.cargo_id ? null : b.cargo_id)}>
                            <td>{openMatBox === b.cargo_id ? '▾' : '▸'} 📦 {b.name}{b.evidence_number ? <span className="text-muted"> · {b.evidence_number}</span> : null}</td>
                            <td style={{ textAlign: 'right' }}>{b.status === 'enroute' ? <strong>1 box</strong> : <span className="text-muted">—</span>}</td>
                            <td style={{ textAlign: 'right' }}>{b.status === 'delivered' ? <strong style={{ color: 'var(--color-success, green)' }}>1 box</strong> : <span className="text-muted">—</span>}</td>
                            <td style={{ textAlign: 'right' }} className="text-muted">{b.contents.length} pol.</td>
                          </tr>
                          {openMatBox === b.cargo_id && b.contents.map((c) => (
                            <tr key={b.cargo_id + c.id}>
                              <td className="text-muted" style={{ fontSize: '0.85rem', paddingLeft: 24 }}>• {c.item_name}</td>
                              <td></td><td></td>
                              <td className="text-muted" style={{ textAlign: 'right', fontSize: '0.85rem' }}>{c.quantity} {c.unit}</td>
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-muted" style={{ fontSize: '0.82rem', marginTop: 8 }}>
                  „Na cestě" = naložené, dosud nepředané. „Na místě" = potvrzeno při předání. Box rozklikni pro obsah.
                </p>
              </>
            )}
          </div>
        )}

        {/* ─── ČASOVÁ OSA ───────────────────────────────────────────────────── */}
        {tab === 'timeline' && (
          <div>
            {!timeline ? <div className="spinner" /> : (
              <>
                <div className="flex" style={{ justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
                  {timeline.length > 0 && <a className="btn btn-outline btn-sm" href={eventsApi.timelineCsvUrl(id)} download>⬇ CSV</a>}
                  <button className="btn btn-outline btn-sm" onClick={loadTimeline}>🔄 Obnovit</button>
                </div>
                <AttendanceTimeline data={timeline} />
              </>
            )}
          </div>
        )}

        {qrZone && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setQrZone(null)}>
            <div className="card text-center" style={{ maxWidth: 360 }} onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-16">{qrZone.name}</h3>
              <img src={zonesApi.qrPreviewUrl(qrZone.id)} alt="QR" style={{ width: 260, height: 260 }} />
              <p className="text-muted mt-8" style={{ fontSize: '0.85rem' }}>kód: {qrZone.slug}</p>
              <div className="flex gap-8 mt-16">
                <a className="btn btn-primary flex-1" href={zonesApi.qrUrl(qrZone.id)}>Stáhnout PNG</a>
                <button className="btn btn-outline" onClick={() => setQrZone(null)}>Zavřít</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
